// Utility to resolve backend-provided message codes into localized,
// parameterized user-facing strings.
//
// The backend typically returns:
// - message_code: string (e.g. "conversation.message_beautified")
// - message: string (default English message)
// - message_data or metadata: object with dynamic values
//
// In the translation JSON files, you should create entries like:
// "conversation.message_beautified": "Message beautified successfully"
// "agent.contact_not_belong_to_agent": "Contact {encoded_contact_id} does not belong to this agent"
//
// NOTE: We intentionally keep single-brace placeholders `{param}` (same
// format as the backend Python templates). This helper performs the
// `{param}` -> value substitution after calling i18next so it works
// with both translated strings and fallback English strings.

/**
 * Resolve a backend message using i18next and then interpolate `{param}`
 * placeholders using the provided message_data / metadata.
 *
 * @param {Function} t - i18next translation function
 * @param {Object} payload - Object that may contain message_code, message, message_data, metadata
 * @returns {string|null} Localized, formatted message or null if nothing usable is provided
 */
export function resolveBackendMessage(t, payload) {
  if (!payload) return null;

  const {
    message_code: messageCode,
    message,
    message_data: messageData,
    metadata,
  } = payload;

  const params = messageData || metadata || {};

  if (!messageCode && !message) {
    return null;
  }

  if (!messageCode) {
    // message is guaranteed truthy here: L38 returns early when both are falsy.
    return message;
  }

  // Let i18next handle language / namespace resolution, but not interpolation.
  const template = t(messageCode, {
    defaultValue: message || messageCode,
  });

  // If there is no dynamic data, return the template as-is.
  if (!params || Object.keys(params).length === 0) {
    return template;
  }

  // Replace `{param}` placeholders using the params object.
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      const value = params[key];
      return value === undefined || value === null ? '' : String(value);
    }
    return match;
  });
}

/**
 * Get a localized message from backend payload or fall back to a local key.
 *
 * @param {Function} t
 * @param {Object} payload - may contain message_code, message, message_data, metadata
 * @param {string} fallbackKey - i18n key to use if payload doesn't resolve
 * @param {Object} [fallbackParams]
 * @returns {string|null}
 */
export function getBackendMessageOr(t, payload, fallbackKey, fallbackParams = {}) {
  const fromBackend = resolveBackendMessage(t, payload);
  if (fromBackend) return fromBackend;
  if (fallbackKey) return t(fallbackKey, fallbackParams);
  return null;
}

